/**
 * Feeder GIS length helpers.
 * Prefer ArcGIS Shape_Length when present; otherwise measure paths in
 * local UTM (matches typical MEPCO GIS exports better than pure geodesic).
 */

const toRad = (deg) => (deg * Math.PI) / 180;

const coordsRingToLatLngPath = (ring) =>
  (ring || [])
    .map((pair) => {
      const lng = Number(pair?.[0]);
      const lat = Number(pair?.[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);

export const extractPathsFromGeoFeature = (feature) => {
  const geom = feature?.geometry;
  if (!geom) return [];
  if (geom.type === 'LineString') {
    return [coordsRingToLatLngPath(geom.coordinates)];
  }
  if (geom.type === 'MultiLineString') {
    return (geom.coordinates || []).map((ring) => coordsRingToLatLngPath(ring));
  }
  return [];
};

const pathFingerprint = (path) => {
  if (!path?.length) return '';
  const forward = path
    .map(({ lat, lng }) => `${lat.toFixed(6)},${lng.toFixed(6)}`)
    .join('|');
  const reverse = [...path]
    .reverse()
    .map(({ lat, lng }) => `${lat.toFixed(6)},${lng.toFixed(6)}`)
    .join('|');
  return forward < reverse ? forward : reverse;
};

/** WGS84 → UTM easting/northing (meters). */
const latLngToUtm = (latDeg, lonDeg) => {
  const zone = Math.floor((lonDeg + 180) / 6) + 1;
  const lonOrigin = (zone - 1) * 6 - 180 + 3;
  const lat = toRad(latDeg);
  const lon = toRad(lonDeg);
  const lon0 = toRad(lonOrigin);
  const a = 6378137.0;
  const f = 1 / 298.257223563;
  const k0 = 0.9996;
  const e2 = f * (2 - f);
  const ep2 = e2 / (1 - e2);
  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const tanLat = Math.tan(lat);
  const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
  const T = tanLat * tanLat;
  const C = ep2 * cosLat * cosLat;
  const A = cosLat * (lon - lon0);
  const M =
    a *
    ((1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 * e2 * e2) / 256) * lat -
      ((3 * e2) / 8 + (3 * e2 * e2) / 32 + (45 * e2 * e2 * e2) / 1024) *
        Math.sin(2 * lat) +
      ((15 * e2 * e2) / 256 + (45 * e2 * e2 * e2) / 1024) * Math.sin(4 * lat) -
      ((35 * e2 * e2 * e2) / 3072) * Math.sin(6 * lat));

  const x =
    k0 *
      N *
      (A +
        ((1 - T + C) * A * A * A) / 6 +
        ((5 - 18 * T + T * T + 72 * C - 58 * ep2) * A * A * A * A * A) / 120) +
    500000;
  const y =
    k0 *
    (M +
      N *
        tanLat *
        (A * A / 2 +
          ((5 - T + 9 * C + 4 * C * C) * A * A * A * A) / 24 +
          ((61 - 58 * T + T * T + 600 * C - 330 * ep2) * A * A * A * A * A * A) /
            720));

  return { x, y };
};

const utmPathLengthMeters = (path) => {
  if (!path || path.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    const a = latLngToUtm(path[i - 1].lat, path[i - 1].lng);
    const b = latLngToUtm(path[i].lat, path[i].lng);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    total += Math.sqrt(dx * dx + dy * dy);
  }
  return total;
};

const haversinePathLengthMeters = (path) => {
  if (!path || path.length < 2) return 0;
  const R = 6371000;
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    const a = path[i - 1];
    const b = path[i];
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h =
      sinLat * sinLat +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
    total += 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  return total;
};

export const computePathLengthMeters = (path) => {
  if (!path || path.length < 2) return 0;
  try {
    const spherical = typeof window !== 'undefined'
      ? window.google?.maps?.geometry?.spherical
      : null;
    // Prefer UTM planar length to align with ArcGIS Shape_Length / Excel exports.
    const utmLen = utmPathLengthMeters(path);
    if (utmLen > 0) return utmLen;
    if (spherical?.computeLength) {
      return spherical.computeLength(path);
    }
  } catch (e) {
    /* fall through */
  }
  return haversinePathLengthMeters(path);
};

const readShapeLengthMeters = (properties) => {
  if (!properties || typeof properties !== 'object') return null;
  const meterKeys = [
    'Shape_Length',
    'SHAPE_Length',
    'Shape_Leng',
    'SHAPE_Leng',
    'Shape Length',
  ];
  for (let i = 0; i < meterKeys.length; i += 1) {
    const value = Number(properties[meterKeys[i]]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  // Some exports store length already in km under Length / LENGTH.
  const kmKeys = ['LENGTH', 'Length', 'length', 'Length_km', 'length_km'];
  for (let i = 0; i < kmKeys.length; i += 1) {
    const value = Number(properties[kmKeys[i]]);
    if (!Number.isFinite(value) || value <= 0) continue;
    if (value < 500) return value * 1000;
    return value;
  }
  return null;
};

/** Length of one GeoJSON feeder feature (deduped path parts), in meters. */
export const computeFeederFeatureLengthMeters = (feature) => {
  const fromProp = readShapeLengthMeters(feature?.properties);
  if (fromProp != null) return fromProp;

  const paths = extractPathsFromGeoFeature(feature);
  const seen = Object.create(null);
  let total = 0;
  paths.forEach((path) => {
    if (!path || path.length < 2) return;
    const fp = pathFingerprint(path);
    if (!fp || seen[fp]) return;
    seen[fp] = true;
    total += computePathLengthMeters(path);
  });
  return total;
};

export const isFeederLineGeoFeature = (feature, { include11kV = false } = {}) => {
  const feederCode = feature?.properties?.['Feeder Code'];
  if (feederCode == null || feederCode === '') return false;
  if (!include11kV && String(feederCode).trim() === '11kV') return false;
  if (feature?.properties?.SHAPE === 'Polygon') return false;
  const type = feature?.geometry?.type;
  return type === 'LineString' || type === 'MultiLineString';
};

/**
 * Sum length for every feeder code across ALL line features.
 * Same Feeder Code may be split into many GeoJSON features.
 */
export const buildFeederTotalLengthByCode = (
  lineFeatures,
  { include11kV = false } = {},
) => {
  const totals = Object.create(null);
  const seenPathsByCode = Object.create(null);

  (lineFeatures || []).forEach((feature) => {
    if (!isFeederLineGeoFeature(feature, { include11kV: true })) return;
    const feederCode = String(feature?.properties?.['Feeder Code'] ?? '').trim();
    if (!feederCode) return;
    if (!include11kV && feederCode === '11kV') return;

    const fromProp = readShapeLengthMeters(feature?.properties);
    if (fromProp != null) {
      totals[feederCode] = (totals[feederCode] || 0) + fromProp;
      return;
    }

    if (!seenPathsByCode[feederCode]) {
      seenPathsByCode[feederCode] = Object.create(null);
    }
    const seen = seenPathsByCode[feederCode];
    const paths = extractPathsFromGeoFeature(feature);
    paths.forEach((path) => {
      if (!path || path.length < 2) return;
      const fp = pathFingerprint(path);
      if (!fp || seen[fp]) return;
      seen[fp] = true;
      totals[feederCode] = (totals[feederCode] || 0) + computePathLengthMeters(path);
    });
  });

  return totals;
};

export const formatFeederLengthKm = (meters) => {
  if (!Number.isFinite(meters) || meters <= 0) return '';
  return `${(meters / 1000).toFixed(2)} km`;
};

export const metersToKmNumber = (meters) => {
  if (!Number.isFinite(meters) || meters <= 0) return 0;
  return Number((meters / 1000).toFixed(2));
};

const pickProp = (properties, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = properties?.[keys[i]];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

/**
 * Excel-style rows:
 * Substation | Feeder Code | Feeder Name | Length (km)
 */
export const buildFeederLengthReportRows = (geoJsonData) => {
  const features = geoJsonData?.features || [];
  const byKey = Object.create(null);
  const seenPathsByKey = Object.create(null);

  features.forEach((feature) => {
    if (!isFeederLineGeoFeature(feature, { include11kV: true })) return;
    const props = feature.properties || {};
    const feederCode = String(props['Feeder Code'] ?? '').trim();
    if (!feederCode) return;

    const feederName = pickProp(props, [
      'Name',
      'FEEDER',
      'Feeder',
      'Feeder Name',
      'feeder_name',
      'FeederName',
    ]);
    const substation = pickProp(props, [
      'Substation',
      'SubStation',
      'Grid',
      'GRID',
      'GridStation',
      'Grid Station',
      'grid',
      'GRID_NAME',
      'Sub_Station',
    ]);

    const key =
      feederCode === '11kV'
        ? `11kV::${feederName || 'unknown'}`
        : feederCode;

    if (!byKey[key]) {
      byKey[key] = {
        substation: substation || '-',
        feederCode,
        feederName: feederName || '-',
        lengthMeters: 0,
      };
      seenPathsByKey[key] = Object.create(null);
    } else {
      if ((!byKey[key].substation || byKey[key].substation === '-') && substation) {
        byKey[key].substation = substation;
      }
      if ((!byKey[key].feederName || byKey[key].feederName === '-') && feederName) {
        byKey[key].feederName = feederName;
      }
    }

    const fromProp = readShapeLengthMeters(props);
    if (fromProp != null) {
      byKey[key].lengthMeters += fromProp;
      return;
    }

    const seen = seenPathsByKey[key];
    extractPathsFromGeoFeature(feature).forEach((path) => {
      if (!path || path.length < 2) return;
      const fp = pathFingerprint(path);
      if (!fp || seen[fp]) return;
      seen[fp] = true;
      byKey[key].lengthMeters += computePathLengthMeters(path);
    });
  });

  return Object.values(byKey)
    .map((row) => ({
      substation: row.substation || '-',
      feederCode: row.feederCode,
      feederName: row.feederName || '-',
      lengthKm: metersToKmNumber(row.lengthMeters),
      lengthMeters: row.lengthMeters,
    }))
    .sort((a, b) => {
      const sub = String(a.substation).localeCompare(String(b.substation));
      if (sub !== 0) return sub;
      return String(a.feederName).localeCompare(String(b.feederName));
    });
};
